import * as React from "react";
import { Tooltip } from "antd";
import { AlertCircle } from "react-feather";
import { Workload } from "../../schema/types/workload/entity";
import {
  SingleNavMenuLink,
  toLinkButton,
  toLinkDivider,
  toLinkExternal,
  toLinkPage,
  toLinkSpace,
} from "../identity/useNavMenu";
import { renderEventWarningIcon } from "../identity/navMenuIcons";
import { formSet, useCpFormAnyValueId, useCpFormModifiedAnyId } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectOrg } from "../../store/contextSlice";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { CacheProcessor } from "./draft/container/CacheProcessor";
import { CpTagsNew } from "../../components/form/CpTagsNew";
import { useEventlogs } from "../../reactHooks/useEventlog";
import { linksOf } from "../../services/cpln";
import { Org } from "../../schema/types/org/entity";
import { formActions } from "../../store/formSlice";
import { WorkloadDetailHeader } from "./detail/header";
import { Summary } from "./detail/summary";
import { WorkloadOptionsTabs } from "./draft/optionsTabs";
import { WorkloadContainersTabs } from "./draft/containersTabs";
import { WorkloadJob } from "./draft/job";
import { WorkloadIdentityLink } from "./draft/identity";
import { WorkloadFirewallConfig } from "./draft/firewallConfig/firewallConfig";
import { WorkloadLoadBalancer } from "./draft/loadBalancer";
import { WorkloadAdvanced } from "./draft/advanced";
import { WorkloadSidecarEnvoy } from "./draft/sidecarEnvoy";
import { JobExecutions } from "./detail/jobExecutions";
import { StatefulDeployments } from "./statefulDeployments/deployments";
import { StatefulReplica } from "./statefulDeployments/replica";
import { DeploymentTable } from "../../components/table/deployment-table";
import { WorkloadConnect } from "./detail/connect";
import { WorkloadLogs } from "./detail/logs";
import { Events } from "../../components/detail/events";
import { AccessReportPage } from "../../components/detail/accessReport";
import { CommandsRoute } from "../../components/detail/command/route";
import { Deployment } from "../../schema/types/workload/deployment";
import { AuditTrail } from "../../components/detail/auditTrail";
import { ImageProcessor } from "./draft/container/ImageProcessor";
import { GvcLocationFetcher } from "./draft/GvcLocationFetcher";
import { Metrics } from "../metrics/metrics";
import { Gvc } from "../../schema/types/gvc/entity";
import { CpRootError } from "../gvc/testNewForm/CpRootError";
import { workloadHelpers } from "../../mst/kinds/workload.helpers";
import { GeoRoutePage, GeoRouteEndpoint } from "../domain/geoRoute/GeoRoutePage";

interface Props {
  workload: Workload;
  org: Org;
  gvc?: Gvc;
  gvcName: string;
  deployments: Deployment[];
  isDeploymentsLoaded: boolean;
  refetch?: () => void;
}
export const WorkloadDetailContent: React.FC<Props> = ({
  workload,
  gvc,
  gvcName,
  org,
  deployments,
  isDeploymentsLoaded,
  refetch,
}) => {
  const dispatch = useAppDispatch();
  const orgName = useAppSelector(selectOrg);
  const basePath = useBasePath(`/gvc/:gvc/workload/:workload/*`);
  const { pathname } = useLocation();

  // eventlog
  const eventlogHook = useEventlogs(linksOf(workload).self!, refetch);
  React.useEffect(() => {
    eventlogHook.setItemLastModified(workload.lastModified);
  }, [workload.lastModified]);
  React.useEffect(() => {
    if (pathname.includes("/events")) {
      return;
    }

    const id = setInterval(() => eventlogHook.fetchEventlogs(true), 30 * 1000);

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [pathname]);

  const { id: formId, submit } = useCpFormContext();

  const type = workload.spec.type;
  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);
  const anyModified = useCpFormModifiedAnyId(formId);

  const health = React.useMemo(() => {
    return workloadHelpers.getHealthFromDeployments(deployments, workload);
  }, [deployments, workload]);

  const geoRouteEndpoints = React.useMemo(() => {
    if (type === "cron") return [];
    const strip = (url: string) => url.split("://").pop()!;
    const endpoints: GeoRouteEndpoint[] = [];
    if (workload.status?.canonicalEndpoint) {
      endpoints.push({ label: "Canonical", endpoint: strip(workload.status.canonicalEndpoint) });
    }
    if (workload.status?.endpoint && workload.status.endpoint !== workload.status.canonicalEndpoint) {
      endpoints.push({ label: "GVC Alias", endpoint: strip(workload.status.endpoint) });
    }
    return endpoints;
  }, [type, workload.status?.canonicalEndpoint, workload.status?.endpoint]);

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Summary", "/summary", { names: [`description`], showDirty: true }),
      toLinkPage("Options", "/options", { names: [`defaultOptions`, `localOptions`], showDirty: true }),
      toLinkPage("Containers", "/containers", {
        names: [`containers`, `containersservestrafficroot`],
        showDirty: true,
      }),
    );

    if (type === "cron") {
      links.push(toLinkPage("Job", "/job", { names: ["job"], showDirty: true }));
    }

    links.push(
      toLinkPage("Identity", "/identity-link", { names: ["identity"], showDirty: true }),
      toLinkPage("Firewall", "/firewall", { names: ["firewallConfig"], showDirty: true }),
    );

    if (type !== "cron") {
      links.push(toLinkPage("Load Balancer", "/load-balancer", { names: ["loadBalancer"], showDirty: true }));
    }

    links.push(
      toLinkPage("Advanced Settings", "/advanced-settings", {
        names: ["supportDynamicTags", "rolloutOptions", "securityOptions", "extras", "requestRetryPolicy"],
        showDirty: true,
      }),
    );

    if (type !== "cron") {
      links.push(toLinkPage("Sidecar | Envoy", "/sidecar-envoy", { names: ["sidecar"], showDirty: true }));
    }

    links.push(
      toLinkPage("Tags", "/tags", { names: ["tags"], showDirty: true }), //
      toLinkDivider(),
    );

    if (workload.spec.type === "cron") {
      links.push(toLinkPage("Job Executions", "/job-executions"));
    } else {
      links.push(
        toLinkPage("Deployments", "/deployments", {
          renderIcon: () => {
            // Don't show error icon while initial fetch is in progress
            if (!isDeploymentsLoaded) {
              return null;
            }
            // Error: not ready OR sync failed
            if (!health.isReady || health.syncFailed) {
              return (
                <span className="absolute right-0 top-0 bottom-0 flex items-center justify-center pr-2">
                  <Tooltip title={health.syncFailed ? "Sync Failed" : "Not Ready"}>
                    <AlertCircle size={16} className="color-danger" />
                  </Tooltip>
                </span>
              );
            }
            // Warning: ready but not latest version
            if (!health.isLatestReady) {
              return (
                <span className="absolute right-0 top-0 bottom-0 flex items-center justify-center pr-2">
                  <Tooltip title="Latest version not ready in all locations">
                    <AlertCircle size={16} className="color-warning" />
                  </Tooltip>
                </span>
              );
            }
            return null;
          },
        }),
      );
    }

    links.push(
      toLinkPage("Connect", "/connect"),
      toLinkPage("Logs", "/logs"),
      toLinkPage("Metrics", "/metrics"),
      // toLinkExternal("Metrics", "metrics-workload"),
    );

    if (geoRouteEndpoints.length > 0) {
      links.push(toLinkPage("Geo Routing", "/geo-routing"));
    }

    if (!!org?.spec?.tracing?.provider?.controlplane) {
      links.push(toLinkExternal("Traces", "traces-workload"));
    }

    links.push(
      toLinkDivider(),
      toLinkPage("Events", "/events", { renderIcon: renderEventWarningIcon(eventlogHook.hasWarning) }),
      toLinkPage("Audit Trail", "/audit-trail"),
      toLinkPage("Access Report", "/access-report"),
      toLinkPage("Commands", "/commands"),
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Update", {
          onClick: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting,
          loading: submitting,
        }),
      );
    }

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [type, anyModified, submitting, health, eventlogHook.hasWarning, geoRouteEndpoints.length]);

  return (
    <BasePathContext.Provider value={basePath}>
      <WorkloadDetailHeader workload={workload} deployments={deployments} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`summary/*`} element={<Summary workload={workload} deployments={deployments} />} />
              <Route path={`options/*`} element={<WorkloadOptionsTabs />} />
              <Route path={`containers/*`} element={<WorkloadContainersTabs />} />
              <Route path={`job/*`} element={<WorkloadJob />} />
              <Route path={`identity-link/*`} element={<WorkloadIdentityLink />} />
              <Route path={`firewall/*`} element={<WorkloadFirewallConfig />} />
              <Route path={`load-balancer/*`} element={<WorkloadLoadBalancer />} />
              <Route path={`advanced-settings/*`} element={<WorkloadAdvanced />} />
              <Route path={`sidecar-envoy/*`} element={<WorkloadSidecarEnvoy />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
              {workload.spec.type === "cron" ? (
                <Route
                  path={`job-executions`}
                  element={<JobExecutions workload={workload} deployments={deployments} />}
                />
              ) : workload.spec.type === "stateful" ? (
                <>
                  <Route
                    path={`deployments`}
                    element={
                      <StatefulDeployments
                        workload={workload}
                        deployments={deployments}
                        isDeploymentsLoaded={isDeploymentsLoaded}
                      />
                    }
                  />
                  <Route
                    path={`deployments/:deployment/replica/:replica/replicaindex/:replicaIndex`}
                    element={<StatefulReplica workload={workload} deployments={deployments} />}
                  />
                </>
              ) : (
                <Route
                  path={`deployments`}
                  element={
                    <DeploymentTable
                      workload={workload}
                      deployments={deployments}
                      isDeploymentsLoaded={isDeploymentsLoaded}
                    />
                  }
                />
              )}
              <Route path={`connect`} element={<WorkloadConnect workload={workload} />} />
              <Route path={`logs`} element={<WorkloadLogs workload={workload} deployments={deployments} />} />
              <Route
                path={`metrics/*`}
                element={<Metrics gvc={[gvcName]} workload={[workload.name]} workloadType={workload.spec.type} />}
              />
              {geoRouteEndpoints.length > 0 ? (
                <Route
                  path={`geo-routing/*`}
                  element={<GeoRoutePage org={orgName} endpoints={geoRouteEndpoints} />}
                />
              ) : null}
              <Route
                path={`events`}
                element={
                  <Events
                    kind={"workload"}
                    eventlogs={eventlogHook.eventlogs}
                    isLoading={eventlogHook.isLoading}
                    fetchEventlogs={eventlogHook.fetchEventlogs}
                    isFetching={eventlogHook.isFetching}
                    nextLink={eventlogHook.nextLink}
                  />
                }
              />
              <Route
                path={`audit-trail`}
                element={
                  <AuditTrail
                    kind={"workload"}
                    itemName={workload.name}
                    resourceId={workload.id}
                    version={workload.version}
                    selfLink={linksOf(workload).self!}
                    refetch={refetch}
                  />
                }
              />
              <Route path={`access-report`} element={<AccessReportPage itemLink={linksOf(workload).self!} />} />
              <Route
                path={`commands/*`}
                element={
                  <CommandsRoute itemLink={linksOf(workload).self!} containerName={workload.spec.containers[0].name} />
                }
              />
              <Route path={`*`} element={<Navigate to={`${basePath}/summary`} replace />} />
            </Routes>
            <CacheProcessor />
            <ImageProcessor />
            <GvcLocationFetcher />
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
